// apps/admin/src/pages/legal-entities/LegalEntitiesPage.tsx
//
// Phase 3.5 Wave 7 Task 21 — Admin Legal Entities CRUD page.
//
// Per CONTEXT.md (Legal Entity multi-entity model: Printway HK
// default + VN/US/SG …, snapshot pattern), (timezone for Phase
// 4.1 invoice cron), (tax_config — VAT/GST/SALES_TAX/NONE).
// Per API-CONTRACTS.md L102-444 (admin legal-entity routes).
// Covers goal: G-FE-ADMIN-LE-01.
//
// Endpoints:
//   GET    /api/v1/admin/legal-entities                (list — q, page, limit, sort)
//   GET    /api/v1/admin/legal-entities/:id            (detail — pre-fill edit form)
//   POST   /api/v1/admin/legal-entities                (create)
//   PUT    /api/v1/admin/legal-entities/:id            (edit — Idempotency-Key + If-Match)
//   PUT    /api/v1/admin/legal-entities/default        (set default — atomic toggle)
//   DELETE /api/v1/admin/legal-entities/:id            (delete — guards: in-use 409, default 422)
//
// Design: Form B (no-asset:phase-3.5-pencil-no-legal-entities-screen) —
// follows DESIGN.md §form-and-table conventions shared by Phase 3.2
// admin pages (TransferGroupsPage / TopupQueuePage).
//
// URL state (R7): ?q=, ?page=, ?sort=, ?dir= synced via useSearchParams.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowUp, ArrowDown } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
  PageHeader,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { apiClient } from '@printway/shared/api';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';
import { useTranslation } from '../../i18n/useTranslation.js';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from '../billing/billing-shared.js';
import {
  LegalEntityForm,
  type LegalEntityDetail,
  type LegalEntityFormState,
  type TaxType,
} from './LegalEntityForm.js';

// ---------------------------------------------------------------------------
// Wire types — list response (API-CONTRACTS L121-143).
// ---------------------------------------------------------------------------

interface LegalEntityRow {
  id: string;
  name: string;
  tax_id: string;
  timezone: string;
  is_default: boolean;
  /** tax_config may be null for legacy entities — migration warning. */
  tax_config: {
    type: TaxType;
    standard_rate: number;
    jurisdiction: string;
  } | null;
  invoice_count: number;
  created_at: string;
}

interface ListResponse {
  data: LegalEntityRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// ---------------------------------------------------------------------------
// URL param helpers — kebab-case naming, page-1 default, server sort enum.
// ---------------------------------------------------------------------------

type SortKey = 'created_at' | 'name';
type SortDir = 'asc' | 'desc';

function readSort(params: URLSearchParams): { key: SortKey; dir: SortDir } {
  const raw = params.get('sort') ?? '-created_at';
  if (raw === 'name') return { key: 'name', dir: 'asc' };
  if (raw === 'created_at') return { key: 'created_at', dir: 'asc' };
  return { key: 'created_at', dir: 'desc' };
}

function serverSortValue(key: SortKey, dir: SortDir): string {
  // Server enum: 'created_at' | '-created_at' | 'name' (per L117 GetLegalEntitiesQuery).
  if (key === 'name') return 'name';
  return dir === 'desc' ? '-created_at' : 'created_at';
}

// ---------------------------------------------------------------------------
// Page component.
// ---------------------------------------------------------------------------

export function LegalEntitiesPage(): JSX.Element {
  const { t } = useTranslation('finance');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- URL-synced controls (R7 R7-list-view requirement) ----
  const q = searchParams.get('q') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const sort = readSort(searchParams);
  // Phase 8.2 audit 2026-05-23 — pageSize is URL-synced per /stores standard,
  // clamped to the allowlist so a hand-typed value can't break the BE cap.
  const rawPageSize = Number.parseInt(searchParams.get('pageSize') ?? '25', 10);
  const limit = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : 25;

  // Debounced search input — local state mirrors URL but updates after 300ms.
  const [searchInput, setSearchInput] = useState<string>(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchInput === q) return;
      const next = new URLSearchParams(searchParams);
      if (searchInput.trim() === '') next.delete('q');
      else next.set('q', searchInput.trim());
      next.delete('page'); // reset paging on filter change
      setSearchParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ---- Data state ----
  const [rows, setRows] = useState<LegalEntityRow[]>([]);
  const [pagination, setPagination] = useState<ListResponse['pagination']>({
    page: 1,
    limit,
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ---- Modal/action state ----
  const [formState, setFormState] = useState<LegalEntityFormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LegalEntityRow | null>(null);
  const [setDefaultTarget, setSetDefaultTarget] = useState<LegalEntityRow | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  // ---- Fetch list whenever URL params change ----
  const fetchList = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const c = apiClient() as unknown as LooseClient;
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sort', serverSortValue(sort.key, sort.dir));
      if (q) params.set('q', q);
      const path = `/api/v1/admin/legal-entities?${params.toString()}`;
      const { data, error, response } = await c.GET(path);
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      const body = unwrap<ListResponse>(data);
      setRows(body.data ?? []);
      setPagination(
        body.pagination ?? { page, limit, total: 0, total_pages: 1 },
      );
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('legal_entity.common.networkDesc');
      setLoadError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // `t` only feeds error copy; adding it would refetch the list on every
    // locale switch (fetchList drives the useEffect below) — intentionally out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort.key, sort.dir, q]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // ---- Sort header click handler — toggles dir for active key, swaps key otherwise ----
  function setSortKey(key: SortKey): void {
    const next = new URLSearchParams(searchParams);
    let nextValue: string;
    if (sort.key === key) {
      const dir = sort.dir === 'asc' ? 'desc' : 'asc';
      nextValue = key === 'name' ? 'name' : dir === 'desc' ? '-created_at' : 'created_at';
    } else {
      nextValue = key === 'name' ? 'name' : '-created_at';
    }
    next.set('sort', nextValue);
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  }

  // ---- Load detail then open edit modal (need full tax_config + bank_info + updated_at). ----
  async function openEdit(row: LegalEntityRow): Promise<void> {
    setEditLoadingId(row.id);
    try {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET(
        `/api/v1/admin/legal-entities/${encodeURIComponent(row.id)}`,
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      const detail = unwrap<LegalEntityDetail>(data);
      setFormState({ mode: 'edit', entity: detail });
    } catch (err) {
      const msg = err instanceof Error
        ? err.message
        : t('legal_entity.common.networkDesc');
      toast({
        variant: 'error',
        title: t('legal_entity.toasts.loadDetailFailed'),
        description: msg,
      });
    } finally {
      setEditLoadingId(null);
    }
  }

  // ---- Set Default mutation (PUT /default with Idempotency-Key) ----
  async function confirmSetDefault(): Promise<void> {
    if (!setDefaultTarget) return;
    const target = setDefaultTarget;
    setBusyRowId(target.id);
    try {
      const c = apiClient() as unknown as LooseClient;
      const { error, response } = await c.PUT(
        `/api/v1/admin/legal-entities/default`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: { legal_entity_id: target.id },
        },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      toast({
        variant: 'success',
        title: t('legal_entity.toasts.setDefaultSuccess'),
        description: target.name,
      });
      setSetDefaultTarget(null);
      await fetchList();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('legal_entity.common.networkDesc');
      toast({
        variant: 'error',
        title: t('legal_entity.toasts.setDefaultFailed'),
        description: msg,
      });
    } finally {
      setBusyRowId(null);
    }
  }

  // ---- Apply-logo-to-all — fan out PUT logo_url to every other entity ----
  async function applyLogoToAll(logoUrl: string, sourceId: string | null): Promise<void> {
    const c = apiClient() as unknown as LooseClient;
    // Iterate the current list (loaded rows) — admin already sees what gets
    // updated. Cross-page propagation is out of scope for v1 of this button.
    const targets = rows.filter((r) => r.id !== sourceId);
    if (targets.length === 0) {
      throw new Error(t('legal_entity.errors.noOtherEntity'));
    }
    const errorsCollected: string[] = [];
    for (const target of targets) {
      try {
        // Use PUT (same wire as form edit). Need If-Match → re-fetch detail
        // for fresh updated_at. We skip the optimistic-lock dance for v1 by
        // sending PATCH-like partial update via PUT body containing only
        // logo_url plus echo of name (so backend Zod min validation passes).
        const detailResp = await c.GET(
          `/api/v1/admin/legal-entities/${encodeURIComponent(target.id)}`,
        );
        if (detailResp.error || !detailResp.response.ok) {
          errorsCollected.push(`${target.name}: detail fetch HTTP ${detailResp.response.status}`);
          continue;
        }
        const detail = unwrap<{ updated_at: string }>(detailResp.data);
        const headers: Record<string, string> = {
          'Idempotency-Key': newIdempotencyKey(),
          'If-Match': detail.updated_at,
        };
        const { error, response } = await c.PUT(
          `/api/v1/admin/legal-entities/${encodeURIComponent(target.id)}`,
          {
            headers,
            body: { logo_url: logoUrl },
          },
        );
        if (error || !response.ok) {
          errorsCollected.push(
            `${target.name}: ${envelopeError(error) ?? `HTTP ${response.status}`}`,
          );
        }
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('legal_entity.logo.unknownError');
        errorsCollected.push(`${target.name}: ${msg}`);
      }
    }
    await fetchList();
    if (errorsCollected.length > 0) {
      throw new Error(
        t('legal_entity.logo.syncPartial', {
          done: targets.length - errorsCollected.length,
          total: targets.length,
          errors: errorsCollected.slice(0, 3).join('; '),
        }),
      );
    }
  }

  // ---- Delete mutation (with 409 LEGAL_ENTITY_IN_USE + 422 CANNOT_DELETE_DEFAULT guards) ----
  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setBusyRowId(target.id);
    try {
      const c = apiClient() as unknown as LooseClient;
      const { error, response } = await c.DELETE(
        `/api/v1/admin/legal-entities/${encodeURIComponent(target.id)}`,
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      if (error || !response.ok) {
        const msg = envelopeError(error) ?? `HTTP ${response.status}`;
        // 409 LEGAL_ENTITY_IN_USE — invoices reference this entity.
        if (response.status === 409) {
          toast({
            variant: 'error',
            title: t('legal_entity.toasts.deleteBlockedTitle'),
            description:
              msg ||
              t('legal_entity.delete.inUseDesc', {
                name: target.name,
                count: target.invoice_count,
              }),
          });
          setDeleteTarget(null);
          return;
        }
        // 422 CANNOT_DELETE_DEFAULT — must change default first.
        if (response.status === 422) {
          toast({
            variant: 'error',
            title: t('legal_entity.delete.defaultBlockedTitle'),
            description: msg || t('legal_entity.delete.defaultBlockedDesc'),
          });
          setDeleteTarget(null);
          return;
        }
        throw new Error(msg);
      }
      toast({
        variant: 'success',
        title: t('legal_entity.toasts.deleteSuccess'),
        description: target.name,
      });
      setDeleteTarget(null);
      await fetchList();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('legal_entity.common.networkDesc');
      toast({
        variant: 'error',
        title: t('legal_entity.toasts.deleteFailed'),
        description: msg,
      });
    } finally {
      setBusyRowId(null);
    }
  }

  // ---- Pagination — canonical @printway/ui Pagination owns window logic ----
  const totalPages = Math.max(1, pagination.total_pages);

  function sortIndicator(key: SortKey): ReactNode {
    if (sort.key !== key) return null;
    return sort.dir === 'asc' ? (
      <ArrowUp className="h-3 w-3" aria-hidden="true" />
    ) : (
      <ArrowDown className="h-3 w-3" aria-hidden="true" />
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      {/* ---- Header ---- */}
      <PageHeader
        title={t('legal_entity.title')}
        subtitle={t('legal_entity.page.subtitle')}
        actions={
          <Button
            variant="default"
            onClick={() => setFormState({ mode: 'create' })}
            data-testid="le-new-entity"
          >
            {t('legal_entity.page.newEntity')}
          </Button>
        }
      />

      {/* ---- Filter row ---- */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-pw-border bg-pw-bg p-3">
        <div className="flex-1 min-w-[240px]">
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('legal_entity.page.searchPlaceholder')}
            data-testid="le-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="le-sort-select" className="text-xs text-pw-text-muted">
            {t('legal_entity.page.sort')}
          </label>
          <Select
            value={serverSortValue(sort.key, sort.dir)}
            onValueChange={(value) => {
              const next = new URLSearchParams(searchParams);
              next.set('sort', value);
              next.delete('page');
              setSearchParams(next, { replace: true });
            }}
          >
            <SelectTrigger id="le-sort-select" data-testid="le-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-created_at">{t('legal_entity.page.sortNewest')}</SelectItem>
              <SelectItem value="created_at">{t('legal_entity.page.sortOldest')}</SelectItem>
              <SelectItem value="name">{t('legal_entity.page.sortName')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ---- Table ---- */}
      <section className="rounded-xl border border-pw-border bg-pw-bg">
        {loading ? (
          <p className="p-6 text-sm text-pw-text-muted">
            {t('legal_entity.page.loading')}
          </p>
        ) : loadError ? (
          <div className="flex items-center justify-between p-6">
            <p className="text-sm text-rose-600 dark:text-rose-300" role="alert">
              {t('legal_entity.page.loadError', { error: loadError })}
            </p>
            <Button variant="secondary" onClick={() => void fetchList()}>
              {t('legal_entity.page.retry')}
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-pw-text-muted">
            {t('legal_entity.page.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="le-table">
              <thead className="border-b border-pw-border bg-pw-surface text-left text-xs uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-pw-text"
                      onClick={() => setSortKey('name')}
                      data-testid="le-sort-name"
                    >
                      {t('legal_entity.columns.legalName')}{' '}
                      {sortIndicator('name')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t('legal_entity.columns.taxId')}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t('legal_entity.columns.taxConfig')}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t('legal_entity.columns.timezone')}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t('legal_entity.columns.invoices')}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-pw-text"
                      onClick={() => setSortKey('created_at')}
                      data-testid="le-sort-created"
                    >
                      {t('legal_entity.columns.created')}{' '}
                      {sortIndicator('created_at')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    {t('legal_entity.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rowBusy = busyRowId === row.id;
                  const editing = editLoadingId === row.id;
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-pw-border last:border-0"
                      data-testid={`le-row-${row.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-pw-text">{row.name}</span>
                          {row.is_default ? (
                            <span
                              className="inline-flex items-center rounded-full bg-pw-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-pw-primary"
                              data-testid={`le-default-badge-${row.id}`}
                            >
                              {t('legal_entity.page.defaultBadge')}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-pw-text-muted">
                        {row.tax_id}
                      </td>
                      <td className="px-4 py-3 text-xs text-pw-text-muted">
                        {row.tax_config ? (
                          <>
                            <span className="font-medium text-pw-text">
                              {row.tax_config.type}
                            </span>
                            {row.tax_config.type !== 'NONE' ? (
                              <>
                                {' · '}
                                {(row.tax_config.standard_rate * 100).toFixed(2)}%
                                {' · '}
                                {row.tax_config.jurisdiction}
                              </>
                            ) : (
                              <>{' · '}{row.tax_config.jurisdiction}</>
                            )}
                          </>
                        ) : (
                          <span
                            className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300"
                            data-testid={`legal-entity-tax-missing-badge-${row.id}`}
                            title={t('legal_entity.page.taxMissingTip')}
                          >
                            {t('legal_entity.page.taxNotConfigured')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.timezone ? (
                          row.timezone
                        ) : (
                          <span
                            className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300"
                            data-testid={`legal-entity-timezone-missing-badge-${row.id}`}
                            title={t('legal_entity.page.timezoneMissingTip')}
                          >
                            {t('legal_entity.page.timezoneNotSet')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-pw-text-muted">
                        {row.invoice_count}
                      </td>
                      <td className="px-4 py-3 text-xs text-pw-text-muted">
                        {new Date(row.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="secondary"
                            disabled={editing || rowBusy}
                            onClick={() => {
                              void openEdit(row);
                            }}
                            data-testid={`le-edit-${row.id}`}
                          >
                            {editing
                              ? t('legal_entity.page.loadingShort')
                              : t('legal_entity.actions.edit')}
                          </Button>
                          <Button
                            variant="secondary"
                            disabled={row.is_default || rowBusy}
                            onClick={() => setSetDefaultTarget(row)}
                            data-testid={`le-set-default-${row.id}`}
                          >
                            {row.is_default
                              ? t('legal_entity.page.defaultBadge')
                              : t('legal_entity.page.setDefault')}
                          </Button>
                          <Button
                            variant="destructive"
                            disabled={rowBusy || row.is_default}
                            onClick={() => setDeleteTarget(row)}
                            data-testid={`le-delete-${row.id}`}
                          >
                            {t('legal_entity.actions.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---- Pagination — canonical /stores footer per audit 2026-05-23 Cluster A. ---- */}
      {pagination.total > 0 ? (
        <PageListFooter
          page={page}
          pageSize={limit}
          total={pagination.total}
          totalPages={totalPages}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            const next = new URLSearchParams(searchParams);
            next.set('pageSize', String(size));
            next.delete('page');
            setSearchParams(next, { replace: true });
          }}
          testId="le-footer"
        />
      ) : null}

      {/* ---- Create / Edit modal ---- */}
      <LegalEntityForm
        state={formState}
        onClose={() => setFormState(null)}
        onSaved={() => {
          void fetchList();
        }}
        onApplyLogoToAll={applyLogoToAll}
      />

      {/* ---- Set Default confirm ---- */}
      <ConfirmDialog
        open={setDefaultTarget != null}
        onOpenChange={(o) => {
          if (!o) setSetDefaultTarget(null);
        }}
        title={t('legal_entity.setDefaultConfirm.title')}
        description={
          setDefaultTarget
            ? t('legal_entity.setDefaultConfirm.desc', {
                name: setDefaultTarget.name,
              })
            : ''
        }
        confirmLabel={t('legal_entity.page.setDefault')}
        type="neutral"
        onConfirm={() => {
          void confirmSetDefault();
        }}
      />

      {/* ---- Delete confirm ---- */}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title={t('legal_entity.deleteConfirm.title')}
        description={
          deleteTarget
            ? t('legal_entity.deleteConfirm.desc', {
                name: deleteTarget.name,
                taxId: deleteTarget.tax_id,
                count: deleteTarget.invoice_count,
              })
            : ''
        }
        confirmLabel={t('legal_entity.actions.delete')}
        type="destructive"
        onConfirm={() => {
          void confirmDelete();
        }}
      />
    </div>
  );
}
